import { type TriggerProps } from '@atlaskit/popup';
import { type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { subscribeTo, key } from '@databases/ping-registry';
import { type PinnedLeadingFieldLayoutItem } from '@databases/pinned-columns/compute-pinned-leading-layout';
import { getPinnedLeadingCellStyle } from '@databases/pinned-columns/pinned-leading-cell';
import {
  getPinnedLeadingBoundaryCellShadowStyle,
  getPinnedLeadingSurfaceGapCoverStyle,
} from '@databases/pinned-columns/pinned-leading-shadow';
import { type HoverProps, useHoverState } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectValueIdFromThreadIdOptional } from '@databases/utils/redux/comments/selectors';
import { getValueId } from '@databases/utils/utils';
import DatabaseValueInlineEdit from '@databases/value/DatabaseValueInlineEdit';
import VisibilityManager from '@databases/visibility-manager';
import composeRefs from '@seznam/compose-react-refs';
import classNames from 'classnames';
import React, { type CSSProperties, type Ref, useEffect, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';

export type TableLayoutCellProps = {
  contentId: string;
  entryId: string;
  fieldId: string;
  rowIndex: number;
  colIndex: number;
  isContentEditable: boolean;
  canEditContent: boolean;
  canEditFields: boolean;
  editingIds: Set<{ entryId: string; fieldId: string | undefined }>;
  setEditingIds: React.Dispatch<
    React.SetStateAction<Set<{ entryId: string; fieldId: string | undefined }>>
  >;

  isFirstInRow: boolean;
  isLastInRow: boolean;
  isLastInColumn: boolean;
  isSelectionDisabled?: boolean;
  isInsertContentDisabled?: boolean;
  showAsDisabled?: boolean;
  addRowBeforeButtonHoverProps: HoverProps;
  addRowAfterButtonHoverProps: HoverProps;
  currentViewId: string;
  dropIndicatorEdge?: Edge | null;
  onIsSelectedChange?: (options?: { isMultiSelectOn?: boolean }) => void;
  handleOnContextMenu?: (event: React.MouseEvent) => void;
  currentSortFieldId?: string;
  pinnedLeadingLayout?: PinnedLeadingFieldLayoutItem;
};

/**
 * During the `databasesSelectionsPerformance`, edits must be kept in parity
 * between TableLayoutCell and LegacyTableLayoutCell.
 */
const TableLayoutCell = React.memo(
  React.forwardRef<HTMLTableSectionElement, TableLayoutCellProps & Partial<TriggerProps>>(
    function TableLayoutCell(
      {
        contentId,
        entryId,
        fieldId,
        rowIndex,
        colIndex,
        isContentEditable,
        canEditContent,
        editingIds,
        setEditingIds,
        isFirstInRow,
        isLastInColumn,
        showAsDisabled,
        onIsSelectedChange,
        currentSortFieldId,
        dropIndicatorEdge,
        pinnedLeadingLayout,
        ...rest
      },
      ref
    ) {
      const cellRef = useRef<HTMLTableCellElement | null>(null);
      const { focusedCommentThreadId, openThread } = useCommentStateContext();
      const isFirstField = rowIndex === 0;

      useEffect(() => {
        const k = key('cell', { entryId, fieldId });
        const unsubscribe = subscribeTo(k, 'selected', (isSelected: boolean) => {
          if (isSelected && cellRef.current && !openThread.valueId) {
            cellRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'nearest',
            });
          }
        });

        return unsubscribe;
      }, [entryId, fieldId, openThread]);

      const [focusedThreadValueId] = defaultStoreUtils.useEqualSelector(
        (state: DefaultRootState) => {
          const focusedThreadValueId = focusedCommentThreadId
            ? selectValueIdFromThreadIdOptional(state, {
                contentId,
                threadId: focusedCommentThreadId,
              })
            : undefined;

          return [focusedThreadValueId] as const;
        }
      );

      const valueId = useMemo(() => {
        return getValueId(entryId, fieldId);
      }, [entryId, fieldId]);

      const initialIsEditing = useMemo(() => {
        for (const { entryId: editingEntryId, fieldId: editingFieldId } of editingIds) {
          if (
            (isFirstField &&
              editingEntryId === entryId &&
              editingFieldId === undefined) ||
            (editingEntryId === entryId && editingFieldId === fieldId)
          ) {
            return true;
          }
        }

        return false;
      }, [editingIds, entryId, fieldId, isFirstField]);

      useEffect(() => {
        const cellElement = cellRef.current;
        const visibilityManager = VisibilityManager.getInstance();

        if (cellElement && focusedThreadValueId === valueId) {
          visibilityManager.observe(cellElement, (el, unobserve) => {
            if (!el.isIntersecting) {
              // requestAnimationFrame is used to ensure that the scroll happens after the DOM update
              globalThis.requestAnimationFrame(() => {
                el.target.scrollIntoView({
                  behavior: 'instant',
                  block: 'center',
                  inline: 'nearest',
                });
              });

              unobserve(el.target);
            }
          });
        }

        return () => {
          visibilityManager.disconnect();
        };
      }, [focusedThreadValueId, valueId]);

      const [isHoverCell, hoverProps, setIsHoverCell] = useHoverState();

      const { isStreaming, operationType } = useDatabaseStreamState();
      const {
        entryIdSelectedForProcessing,
        isSingleFieldStreaming,
        currentEntryOperation,
      } = useSnapshot(aiFirstCreationStreamingState);
      const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
      const isEditProcessing =
        isStreaming &&
        isDbAiFirstCreationEnabled &&
        operationType === 'edit' &&
        entryId === entryIdSelectedForProcessing &&
        currentEntryOperation === 'edit';

      const pinnedLeadingStyle = useMemo(
        () => getPinnedLeadingCellStyle(pinnedLeadingLayout),
        [pinnedLeadingLayout]
      );
      const pinnedLeadingGapCoverStyle =
        pinnedLeadingLayout?.isPinned === true && colIndex > 0
          ? getPinnedLeadingSurfaceGapCoverStyle()
          : undefined;
      const pinnedLeadingBoundaryShadowStyle =
        pinnedLeadingLayout?.isFreezeBoundary === true
          ? getPinnedLeadingBoundaryCellShadowStyle()
          : undefined;
      const restRecord = rest as { style?: CSSProperties };
      const mergedStyle =
        pinnedLeadingStyle !== undefined
          ? { ...restRecord.style, ...pinnedLeadingStyle }
          : restRecord.style;

      return (
        <td
          {...rest}
          {...hoverProps}
          style={mergedStyle}
          data-testid={'database-cell'}
          data-field-id={fieldId}
          data-entry-id={entryId}
          data-row-index={rowIndex}
          data-col-index={colIndex}
          data-dbs-pinned-leading={pinnedLeadingStyle ? 'true' : undefined}
          ref={composeRefs(cellRef, ref as Ref<HTMLTableCellElement>)}
          className={classNames([
            {
              [TABLE_CLASSNAMES.SHOW_AS_DISABLED]: showAsDisabled,
              [TABLE_CLASSNAMES.IS_ROVO_EDIT_PROCESSING]:
                isEditProcessing && !isSingleFieldStreaming,
            },
          ])}
        >
          {pinnedLeadingGapCoverStyle && (
            <div
              data-testid="pinned-leading-cell-surface-gap-cover"
              style={pinnedLeadingGapCoverStyle}
            />
          )}
          {pinnedLeadingBoundaryShadowStyle && (
            <div
              data-testid="pinned-leading-cell-boundary-shadow"
              style={pinnedLeadingBoundaryShadowStyle}
            />
          )}
          <DatabaseValueInlineEdit
            contentId={contentId}
            fieldId={fieldId}
            entryId={entryId}
            initialIsEditing={initialIsEditing}
            setEditingIds={setEditingIds}
            isReadonly={!canEditContent}
            isContentEditable={isContentEditable}
            onIsSelectedChange={onIsSelectedChange}
            isFirstInRow={isFirstInRow}
            matchHeaderPadding={true}
            isLastInColumn={isLastInColumn}
            enableSelection
            isHoverCell={isHoverCell}
            setIsHoverCell={setIsHoverCell}
            currentSortFieldId={currentSortFieldId}
          />
          {dropIndicatorEdge && <DropIndicator edge={dropIndicatorEdge} />}
        </td>
      );
    }
  )
);

export default TableLayoutCell;
